import { useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'

// API функции для PDF файлов
const getPrintPdfFiles = async (projectId: number) => {
  const response = await fetch(`/api/print/pdf-files?project_id=${projectId}`)
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to fetch PDF files')
  return data.data
}

const uploadPrintPdfFile = async (formData: FormData) => {
  const response = await fetch('/api/print/pdf-files', {
    method: 'POST',
    body: formData
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to upload PDF file')
  return data.data
}

const deletePrintPdfFile = async (fileId: number) => {
  const response = await fetch(`/api/print/pdf-files/${fileId}`, {
    method: 'DELETE'
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to delete PDF file')
  return data
}

const downloadPrintPdfFile = async (fileId: number) => {
  const response = await fetch(`/api/print/pdf-files/${fileId}/download`)
  if (!response.ok) throw new Error('Failed to download PDF file')
  
  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `pdf_${fileId}.pdf`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

interface PrintPdfFile {
  id: number
  project_id: number
  filename: string
  file_path: string
  file_size: number
  generated_at: string
  description: string
  sheet_id?: number
  dpi?: number
  created_at: string
}

export default function PrintPdfFilesPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadDescription, setUploadDescription] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: pdfFiles = [], isLoading } = useQuery({
    queryKey: ['print-pdf-files', projectId],
    queryFn: () => getPrintPdfFiles(Number(projectId!)),
    enabled: !!projectId,
  })

  const uploadFileMutation = useMutation({
    mutationFn: uploadPrintPdfFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-pdf-files', projectId] })
      setIsUploadModalOpen(false)
      setUploadDescription('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
  })

  const deleteFileMutation = useMutation({
    mutationFn: deletePrintPdfFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-pdf-files', projectId] })
    },
  })

  const handleFileUpload = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!fileInputRef.current?.files?.[0]) return
    
    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('project_id', projectId!)
      formData.append('file', fileInputRef.current.files[0])
      formData.append('description', uploadDescription)
      
      await uploadFileMutation.mutateAsync(formData)
    } catch (error) {
      console.error('Upload failed:', error)
      alert('Ошибка загрузки файла: ' + error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteFile = async (fileId: number, filename: string) => {
    if (!confirm(`Удалить PDF файл "${filename}"?`)) return
    
    try {
      await deleteFileMutation.mutateAsync(fileId)
    } catch (error) {
      console.error('Delete failed:', error)
      alert('Ошибка удаления файла: ' + error)
    }
  }

  const handleDownloadFile = async (fileId: number) => {
    try {
      await downloadPrintPdfFile(fileId)
    } catch (error) {
      console.error('Download failed:', error)
      alert('Ошибка скачивания файла: ' + error)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Б'
    const k = 1024
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return <div className="text-center py-8">Загрузка PDF файлов...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к проекту
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">PDF файлы</h1>
          <p className="text-gray-600 mt-1">
            {project?.name} - Управление PDF файлами
          </p>
        </div>
        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          📤 Загрузить PDF
        </button>
      </div>

      {/* PDF Files List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            PDF файлы ({pdfFiles.length})
          </h2>
        </div>
        
        {pdfFiles.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg mb-2">Нет загруженных PDF файлов</p>
            <p className="text-sm">Загрузите первый PDF файл или экспортируйте листы для печати</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Файл
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Описание
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Размер
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата создания
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pdfFiles.map((file: PrintPdfFile) => (
                  <tr key={file.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-red-500 mr-2">📄</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {file.filename}
                          </div>
                          {file.sheet_id && (
                            <div className="text-xs text-gray-500">
                              Лист #{file.sheet_id}
                              {file.dpi && ` • DPI: ${file.dpi}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={file.description}>
                        {file.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(file.file_size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(file.generated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDownloadFile(file.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Скачать"
                        >
                          📥
                        </button>
                        <button
                          onClick={() => handleDeleteFile(file.id, file.filename)}
                          className="text-red-600 hover:text-red-900"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Загрузить PDF файл</h3>
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Выберите PDF файл
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание (опционально)
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Описание файла..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {isUploading ? '🔄 Загрузка...' : 'Загрузить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
